import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import {
  DetailList,
  DetailListSkeleton,
  type DetailGroup,
} from "@/components/DetailList";
import { EnrollmentChecklist } from "@/components/EnrollmentChecklist";
import { OnlineStatusBadge } from "@/components/OnlineStatusBadge";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { HeartbeatValue } from "./HeartbeatValue";
import { SetupDisclosure } from "./SetupDisclosure";

interface GatewayDetailsData {
  id: string;
  name: string;
  source: string;
  createdAt: string;
  publicUrl: string | null;
  lastSeenAt: string | null;
  userAgent: string | null;
  platform: string | null;
}

export function GatewayDetails() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, gatewayId } = useParams();
  const [gateway, setGateway] = useState<GatewayDetailsData | null>(null);

  const loadGateway = useCallback(() => {
    if (!accessToken || !orgId || !gatewayId) {
      return;
    }

    void api
      .get<GatewayDetailsData>(`/organizations/${orgId}/gateways/${gatewayId}`)
      .then(setGateway);
  }, [accessToken, api, orgId, gatewayId]);

  useEffect(loadGateway, [loadGateway]);

  useEffect(() => {
    const interval = setInterval(loadGateway, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadGateway]);

  if (!gateway) {
    return <DetailListSkeleton rows={6} />;
  }

  const isSelfHosted = gateway.source !== "Hosted";
  const hasNeverConnected = isSelfHosted && !gateway.lastSeenAt;

  const groups: DetailGroup[] = [
    {
      title: "Connection",
      rows: [
        {
          label: "Status",
          value: <OnlineStatusBadge lastSeenAt={gateway.lastSeenAt} />,
        },
        {
          label: "Last heartbeat",
          value: <HeartbeatValue lastSeenAt={gateway.lastSeenAt} />,
        },
        {
          label: "Public URL",
          value: gateway.publicUrl ?? "Unknown",
        },
      ],
    },
    {
      title: "Host",
      rows: [
        {
          label: "Type",
          value: isSelfHosted ? "Self-hosted" : "Hosted",
        },
        {
          label: "Client version",
          value: gateway.userAgent ?? "Unknown",
        },
        {
          label: "Platform",
          value: gateway.platform ?? "Unknown",
        },
        { label: "Created", value: formatDate(gateway.createdAt) },
      ],
    },
  ];

  return (
    <div className="space-y-5">
      {hasNeverConnected && (
        <EnrollmentChecklist
          entityType="gateway"
          createdAt={gateway.createdAt}
          lastSeenAt={gateway.lastSeenAt}
        />
      )}

      <DetailList groups={groups} />

      {isSelfHosted && !hasNeverConnected && (
        <SetupDisclosure
          entityType="gateway"
          createdAt={gateway.createdAt}
          lastSeenAt={gateway.lastSeenAt}
        />
      )}
    </div>
  );
}
